import { AxiosRequestConfig } from 'axios';

/**
 * Ensure that urls for the same resource are exact string matches.
 * @param url the url to normalize
 * @returns the normalized url
 */
export function normalizeRequestUrl(url: string | undefined): string | null {
    if (!url) {
        return null;
    }

    // remove query string from url since sometimes query string hasn't been processed.
    url = url.replace(/\?.*/g, '');

    if (url[0] !== '/') {
        return '/' + url;
    }

    return url;
}

function extractParams(url: string | undefined): Record<string, string> | undefined {
    if (!url) {
        return undefined;
    }

    const params: Record<string, string> = {};
    url.substring(url.indexOf('?') + 1)
        .split('&')
        .forEach((param) => {
            const [k, v] = param.split('=');
            params[decodeURIComponent(k)] = decodeURIComponent(v);
        });

    return params;
}

function stringifyParamValues(params: Record<string, any> | undefined): Record<string, string> | undefined {
    if (!params) {
        return params;
    }

    const newParams: Record<string, string> = {};
    Object.keys(params).forEach((key) => {
        newParams[key] = String(params[key]);
    });

    return newParams;
}

/**
 * Deeply sorts an object's keys alphabetically to ensure consistent JSON stringification
 * @param obj The object to sort
 * @returns A new object with sorted keys
 */
function deepSortByKey<T>(obj: T): T {
    if (Array.isArray(obj)) {
        return obj.map(deepSortByKey) as unknown as T;
    }

    if (typeof obj === 'object' && obj !== null) {
        const sortedKeys = Object.keys(obj).sort();
        const newObj: Record<string, any> = {};
        sortedKeys.forEach((k) => {
            newObj[k] = deepSortByKey((obj as Record<string, any>)[k]);
        });
        return newObj as unknown as T;
    }

    return obj;
}

/**
 * Return a string cache key for the request object (all in lowercase)
 * @param request to retrieve key for
 * @returns the request's key
 */
export function requestToKey(request: AxiosRequestConfig): string {
    return JSON.stringify(
        deepSortByKey({
            url: normalizeRequestUrl(request.url),
            params: stringifyParamValues(request.params ?? extractParams(request.url)) ?? null,
            cacheKeySuffix: request.cacheKeySuffix ?? null,
        })
    ).toLowerCase();
}
